// The shared gazetteer index (`{ byId, byKey }`) for a page that displays place
// names -- route maps, From/To columns, the built-up titles behind them.
//
// State rather than a ref: everything downstream is a memo or a render, and
// they have to recompute when the index lands.
//
// Deliberately per-mount rather than an app-level provider. `loadPlaceIndex`
// reads through `places.mjs`'s 10-minute cache, so a second page pays nothing;
// what a shared snapshot WOULD cost is correctness -- editing a row in
// /me/places invalidates that module cache, and a provider holding its own copy
// would keep serving the old name until something forced it to re-render.
// Mounting a page is the natural moment to ask again.
//
// `enabled: false` skips the fetch entirely, for a page that only sometimes has
// a place column to render (a Maintenance logbook has none, and `loadPlaceIndex`
// is a whole-table read). It resolves to `null`, which every consumer already
// treats as "leave the names alone".
//
// Never throws: `loadPlaceIndex` resolves to empty maps on failure, which
// `resolvePlaceName` and `resolveRouteAgainstPlaces` both read as "the
// gazetteer had nothing to say".

import { useEffect, useState } from 'react';
import { loadPlaceIndex } from './places.mjs';

export function usePlaceIndex({ enabled = true } = {}) {
  const [placeIndex, setPlaceIndex] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;
    loadPlaceIndex().then((index) => {
      if (mounted) setPlaceIndex(index);
    });
    return () => { mounted = false; };
  }, [enabled]);

  return placeIndex;
}

export default usePlaceIndex;
